"use client";

import React, { createContext, useState, useEffect, useContext } from 'react';
import {
  ThemeConfig,
  DEFAULT_THEME,
  getActiveTheme,
  applyTheme
} from '../services/ThemeService';

// Check if code is running in browser environment
const isBrowser = typeof window !== 'undefined';

// Define theme context interface
interface ThemeContextType {
  theme: ThemeConfig;
  setTheme: (theme: ThemeConfig) => void;
  applyTheme: (theme: ThemeConfig) => void;
  isLoading: boolean;
}

// Create the context with default values
const ThemeContext = createContext<ThemeContextType>({
  theme: DEFAULT_THEME,
  setTheme: () => { },
  applyTheme: () => { },
  isLoading: true,
});

// Theme provider component
export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // State for current theme and loading status
  const [theme, setThemeState] = useState<ThemeConfig>(DEFAULT_THEME);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Load active theme on mount
  useEffect(() => {
    const loadTheme = async () => {
      try {
        setIsLoading(true);
        const activeTheme = await getActiveTheme();
        setThemeState(activeTheme);
        applyTheme(activeTheme); // Apply theme to document
      } catch (error) {
        console.error('Error loading theme:', error);
        // Apply default theme on error
        applyTheme(DEFAULT_THEME);
      } finally {
        setIsLoading(false);
      }
    };

    loadTheme();
  }, []);

  // Set theme function that also applies the theme
  const setTheme = (newTheme: ThemeConfig) => {
    setThemeState(newTheme);
    applyTheme(newTheme);
    // Save active theme ID in localStorage (only in browser environment)
    if (isBrowser && newTheme.id) {
      localStorage.setItem('activeThemeId', newTheme.id);
    }
  };

  // Create context value
  const contextValue: ThemeContextType = {
    theme,
    setTheme,
    applyTheme,
    isLoading,
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

// Custom hook for using the theme context
export const useTheme = () => useContext(ThemeContext);

export default ThemeContext;